import { useCallback, useEffect } from 'react';
import {
  CANVAS_SITE_CONTEXT_TOOLBAR_EVENT,
  autoArrangeSiteContextPanels,
  getEditor,
  openPanelInCanvas,
  type SiteContextToolbarAction,
  type SiteContextToolbarEventDetail,
  type SiteContextToolbarPanelId,
} from 'agentable-canvas/whiteboard';
import { useCanvasSession } from '../../contexts/CanvasSessionContext';
import { useUiOverlay } from '../../contexts/useUiOverlay';
import { openOnboardingChatPanel, openProjectBriefPanel } from '../../hooks/useOnboardingPanelsBridge';
import { openFileManagerPanel } from '../../lib/canvas-tool-actions';
import { saveCanvasSnapshotNow } from '../../lib/save-canvas-snapshot';

function dispatchQuickNavSettings(siteId: string): void {
  window.dispatchEvent(
    new CustomEvent('landi:canvas-quicknav', {
      detail: { action: 'settings', siteId, timestamp: new Date().toISOString() },
      bubbles: true,
      composed: true,
    }),
  );
}

function openSiteContextPanel(panelId: SiteContextToolbarPanelId, siteId: string): void {
  switch (panelId) {
    case 'chat':
      openOnboardingChatPanel();
      return;
    case 'project-brief':
    case 'settings':
      openProjectBriefPanel();
      if (panelId === 'settings') {
        dispatchQuickNavSettings(siteId);
      }
      return;
    case 'web-preview':
      openPanelInCanvas('web-preview', {
        focus: true,
        preserveZoom: true,
        panelProps: {
          siteId,
          __siteId: siteId,
          __title: 'Preview',
          __hideChrome: true,
          __noBorder: true,
          __fullBleed: true,
        },
      });
      return;
    case 'file-manager':
    case 'assets':
      openFileManagerPanel(siteId);
      return;
    case 'snapshots':
      void saveCanvasSnapshotNow(siteId);
      return;
    default: {
      const _exhaustive: never = panelId;
      return _exhaustive;
    }
  }
}

/** Host bridge for site-context contextual toolbar actions from agentable-canvas. */
export function SiteContextToolbarBridge(): null {
  const { publishSite } = useCanvasSession();
  const { hideHome } = useUiOverlay();

  const handleAction = useCallback(
    (action: SiteContextToolbarAction): void => {
      hideHome();

      switch (action.type) {
        case 'open-panel':
          openSiteContextPanel(action.panelId, action.siteId);
          return;
        case 'auto-arrange': {
          const editor = getEditor();
          if (editor) {
            autoArrangeSiteContextPanels(editor, action.siteId);
          }
          return;
        }
        case 'publish':
          void publishSite();
          return;
        case 'save':
          void saveCanvasSnapshotNow(action.siteId);
          return;
        default: {
          const _exhaustive: never = action;
          return _exhaustive;
        }
      }
    },
    [hideHome, publishSite],
  );

  useEffect(() => {
    const handler = (event: Event): void => {
      const detail = (event as CustomEvent<SiteContextToolbarEventDetail>).detail;
      if (!detail?.action) return;
      handleAction(detail.action);
    };

    window.addEventListener(CANVAS_SITE_CONTEXT_TOOLBAR_EVENT, handler);
    return () => window.removeEventListener(CANVAS_SITE_CONTEXT_TOOLBAR_EVENT, handler);
  }, [handleAction]);

  return null;
}
